import { Button } from "../components/Button";
import { useLocation } from "wouter";
import React, { useEffect, useState, useCallback } from "react";
import { handleCreateRoom } from "~/api/roomApi";
import { useSnapshot } from "valtio";
import { CartPage, loadCartIds, localState } from "~/state/state";
import { UserProfile, getUser, hasFullAccess} from "~/api/userApi";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { StyledProfilePic } from "../components/ProfilePic";
import { imageUrl } from "~/utils/imageurl";
import { trackEvent } from "~/api/analyticsApi";
import { keyframes, styled, darkTheme } from "../style/stitches.config";
import { getNotifications, markNotificationsRead, Notification } from "~/api/communityApi";

// Helper function to format time ago
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  const diffWeeks = Math.floor(diffInSeconds / 604800);
  if (diffWeeks <= 4) return `${diffWeeks}w ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
// import { videoAsk } from "../../utils/videoask";

const ProfileMenuItem = ({
  src,
  text,
  onClick,
}: {
  src: string;
  text: string;
  onClick: Function;
}) => {
  return (
    <StyledProfileMenuItem
      id={`profile-menu-${text}`}
      onClick={onClick as React.MouseEventHandler}
    >
      <img className="icon" src={src}></img>
      <div className="text">{text}</div>
    </StyledProfileMenuItem>
  );
};

export const TopBar = ({
  user,
  signout,
}: {
  user: UserProfile | null;
  signout: Function;
}) => {
  const [location, setLocation] = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isInviteMenuOpen, setIsInviteMenuOpen] = useState(false);
  const [isLinkCopyOpen, setIsLinkCopyOpen] = useState(false);
  const {
    isConnecting,
    isConnected,
    inDarkMode,
    appDarkMode,
    isInRoom,
    isOnProProfile,
    editedProfilePic,
    isOnMobile,
    isOnPlusPlan,
    currCartIds,
    isRouletteRoom,
  } = useSnapshot(localState);

  // top bar follows the room/pro's theme in those contexts, otherwise the viewer's app theme
  const topBarDark = isInRoom || isOnProProfile ? inDarkMode : appDarkMode;

  useEffect(() => {
    loadCartIds();
  }, []);

  useEffect(() => {
    if (isProfileMenuOpen) {
      document
        .getElementById("profile-menu-Feedback")!
        .setAttribute(
          "onclick",
          "videoask.loadModal('https://www.videoask.com/fcaqxaw1l')"
        );
    }
  }, [isProfileMenuOpen]);

  const [profilePic, setProfilePic] = useState("");
  useEffect(() => {
    (async () => {
      if (!user?.uuid) return;
      getUser(user?.uuid).then((res) => {
        setProfilePic(res.profilePic);
      });
    })();
    localState.editedProfilePic = false;
  }, [editedProfilePic, user]);

  useEffect(() => {
    if (!user) setIsProfileMenuOpen(false);
  }, [user]);

  // Notification state
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const response = await getNotifications();
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount);

      // Post unread count to iOS native app via JS bridge
      (window as any).webkit?.messageHandlers?.notificationCount?.postMessage(response.unreadCount);

      // Check if there are new community posts since last visit
      if (response.latestPostAt) {
        const lastVisited = localStorage.getItem('community_last_visited');
        localState.hasNewCommunityPosts = !lastVisited || new Date(response.latestPostAt).getTime() > Number(lastVisited);
      } else {
        localState.hasNewCommunityPosts = false;
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [user]);

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark all as read when dropdown opens
  useEffect(() => {
    if (isNotificationMenuOpen && unreadCount > 0) {
      // Mark as read on backend immediately (bell dot disappears)
      markNotificationsRead().then(() => {
        setUnreadCount(0);
        // Tell iOS native app the count is now 0
        (window as any).webkit?.messageHandlers?.notificationCount?.postMessage(0);
      }).catch(() => {});
      // Delay updating local UI so red dots linger for a few seconds
      const timer = setTimeout(() => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isNotificationMenuOpen, unreadCount]);

  // Close notification menu when clicking elsewhere or when profile menu opens
  useEffect(() => {
    if (isProfileMenuOpen) {
      setIsNotificationMenuOpen(false);
    }
  }, [isProfileMenuOpen]);

  // hide TopBar on iOS Natve if in room or connecting
  if (isIOSNativeApp() && (isInRoom || isConnecting)) {
    return <div style={{ height: 0, visibility: 'hidden' }} />;
  }

  // hide TopBar on iOS Native if on billing page
  if (isIOSNativeApp() && window.location.pathname.includes("/plans")) {
    return <div style={{ height: 0, visibility: 'hidden' }} />;
  }

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setIsLinkCopyOpen(true);
      window.setTimeout(() => setIsLinkCopyOpen(false), 3000);
    });
  };

  const profileMenu = (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", position: "relative" }}
      >
        {isConnected && isInRoom && !isRouletteRoom && (
          <>
            <div style={{ position: "relative", marginRight: "6px" }}>
              <Button
                style={{
                  height: "28px",
                  padding: "0 16px 0 12px",
                  fontSize: "12px",
                  borderRadius: "9px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: !inDarkMode
                    ? "1px solid transparent"
                    : "1px solid black",
                  margin: 0,
                }}
                animateIn
                darkHover={!inDarkMode}
                onClick={() => {
                  trackEvent("/room/invite");
                  setIsInviteMenuOpen(!isInviteMenuOpen);
                }}
              >
                <img
                  style={{
                    width: "18px",
                    height: "18px",
                    marginRight: "4px",
                    marginTop: "-1px",
                    marginLeft: 0,
                    filter: !inDarkMode ? "none" : "invert(1)",
                  }}
                  className="icon"
                  src="/images/room/invite.svg"
                ></img>
                Host
              </Button>
              {isInviteMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "50px",
                    right: "0",
                    width: "154px",
                    height: isOnMobile ? "170px" : "150px",
                    padding: "30px 16px 16px 16px",
                    borderRadius: "25px",
                    color: inDarkMode ? "white" : "black",
                    border: !inDarkMode ? "1px solid black" : "1px solid white",
                    backgroundColor: !inDarkMode ? "white" : "black",
                  }}
                >
                  <div
                    className="back-button arrow-button"
                    style={{
                      position: "absolute",
                      top: isOnMobile ? "3px" : "8px",
                      right: "10px",
                      width: "20px",
                      transform: "rotate(90deg)",
                      padding: "2px 6px",
                      fontSize: isOnMobile ? "18px" : "14px",
                      cursor: "pointer",
                      color: inDarkMode ? "white" : "black",
                    }}
                    onClick={() => setIsInviteMenuOpen(false)}
                  >
                    {" "}
                    &larr;
                  </div>
                  <div
                    className="text"
                    style={{ fontSize: isOnMobile ? "12px" : "10px" }}
                  >
                    Share this link to invite anyone into your room. Up to 6
                    people can join.
                  </div>
                  <StyledLinkInput
                    type="text"
                    onClick={handleLinkCopy}
                    className="link"
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      fontSize: "10px",
                      textOverflow: "ellipsis",
                      width: "100%",
                      display: "block",
                      height: "25px",
                      marginBottom: "4px",
                      borderRadius: "32px",
                      whiteSpace: "nowrap",
                      paddingLeft: "8px",
                      marginTop: "12px",
                      cursor: "pointer",
                      backgroundColor: inDarkMode ? "black" : "white",
                      border: inDarkMode
                        ? "1px solid white"
                        : "1px solid black",
                    }}
                    value={window.location.href}
                  />

                  <Button
                    style={{
                      height: "28px",
                      width: "100%",
                      padding: "0 16px 0 12px",
                      fontSize: isOnMobile ? "12px" : "10px",
                      borderRadius: "25px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: !inDarkMode
                        ? "1px solid white"
                        : "1px solid black",
                      color: !inDarkMode ? "white" : "black",
                      margin: 0,
                    }}
                    darkHover={!inDarkMode}
                    onClick={handleLinkCopy}
                  >
                    {!isLinkCopyOpen && (
                      <img
                        style={{
                          width: "18px",
                          height: "18px",
                          marginRight: "4px",
                          marginTop: "-1px",
                          marginLeft: 0,
                          filter: !inDarkMode ? "none" : "invert(1)",
                        }}
                        className="icon"
                        src="/images/room/copy.svg"
                      ></img>
                    )}
                    {isLinkCopyOpen ? "Link copied!" : "Copy link"}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
        {isConnected && isInRoom && (
          <>
            {isRouletteRoom && (
              <Button
                style={{
                  height: "28px",
                  padding: "0 16px",
                  fontSize: "12px",
                  borderRadius: "9px",
                  marginRight: "6px",
                  backgroundColor: inDarkMode ? "black" : "white",
                  color: inDarkMode ? "white" : "black",
                  border: inDarkMode ? "1px solid white" : "1px solid black",
                }}
                animateIn
                onClick={() => {
                  localState.showMicCheck = true;
                }}
              >
                Test mic
              </Button>
            )}
            <Button
              style={{
                height: "28px",
                padding: "0 16px",
                fontSize: "12px",
                borderRadius: "9px",
                marginRight: isRouletteRoom ? "12px" : "0px",
                border: inDarkMode ? "1px solid white" : "1px solid black",
              }}
              animateIn
              darkHover={isRouletteRoom ? !inDarkMode : inDarkMode}
              onClick={() => {
                // Clean up call before leaving roulette room
                if (isRouletteRoom) {
                  localState.isInRoom = false;
                  localState.isRouletteRoom = false;
                }
                
                const leaveUrl = isRouletteRoom 
                  ? `/leave?type=roulette${localState.matchedUsername ? `&partner=${encodeURIComponent(localState.matchedUsername)}` : ''}` 
                  : "/leave";
                setLocation(leaveUrl);
                setIsProfileMenuOpen(false);
                localState.isInRoom = false;
                localState.isRouletteRoom = false;
              }}
            >
              Leave
            </Button>
          </>
        )}
        {!isInRoom &&
          !isConnecting &&
          !window.location.pathname.includes("video") && (
            <>
              {!isIOSNativeApp() &&
              !window.location.pathname.includes("profile") &&
              !window.location.pathname.includes("plans") &&
              !window.location.pathname.includes("journal") &&
              !window.location.pathname.includes("daily") &&
              !window.location.pathname.includes("game") &&
              !window.location.pathname.includes("community") &&
              (
                <Button
                  id="cart-button"
                  Theme={topBarDark ? "dark" : "light"}
                  style={{
                    height: "26px",
                    padding: "0 16px",
                    fontSize: "12px",
                    borderRadius: "9px",
                    margin: 0,
                    marginRight: "4px",
                    border: !topBarDark
                      ? "1px solid black"
                      : "1px solid white",
                  }}
                  animateIn
                  onClick={() => {
                    localState.cartPage = CartPage.CART;
                  }}
                >
                  <img src="/images/cart.svg" style={{ filter: topBarDark ? "invert(1)" : "none" }} />
                  {currCartIds.length > 0 ? `${currCartIds.length}` : ""}
                </Button>
              )}
              {/* Notification heart button */}
              {!isRouletteRoom && !(isIOSNativeApp() && (window.location.pathname.includes("guidebook") || window.location.pathname.includes("decks"))) && (
                <Button
                  id="notification-button"
                  Theme={topBarDark ? "dark" : "light"}
                  style={{
                    height: "26px",
                    padding: "0 16px",
                    fontSize: "12px",
                    borderRadius: "9px",
                    margin: 0,
                    marginRight: "4px",
                    border: !topBarDark
                      ? "1px solid black"
                      : "1px solid white",
                    position: 'relative',
                  }}
                  animateIn
                  onClick={() => {
                    trackEvent("/topbar/pressed_notifications");
                    setIsNotificationMenuOpen(!isNotificationMenuOpen);
                    setIsProfileMenuOpen(false);
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.7601 10.4741L4.74429 10.4587L4.72719 10.4448C3.97439 9.83148 3.49414 8.8963 3.49414 7.8476C3.49414 6 4.98569 4.50549 6.82158 4.50549C7.97163 4.50549 8.98634 5.09117 9.58494 5.98516L10.0004 6.60564L10.4159 5.98516C11.0145 5.09117 12.0292 4.50549 13.1792 4.50549C15.0151 4.50549 16.5067 6 16.5067 7.8476C16.5067 8.89841 16.0245 9.83526 15.2691 10.4485L15.2516 10.4627L15.2355 10.4784L10.1395 15.4378C10.0618 15.5134 9.93813 15.5134 9.86049 15.4378L4.7601 10.4741Z"
                      stroke={topBarDark ? "white" : "black"}
                    />
                  </svg>
                  {unreadCount > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '4.5px',
                        right: '8px',
                        width: '9px',
                        height: '9px',
                        backgroundColor: '#ff564d',
                        borderRadius: '50%',
                        border: '1px solid #fff',
                      }}
                    />
                  )}
                </Button>
              )}
              {!isIOSNativeApp() && (
                <Button
                  id="create-room-button"
                  style={{
                    height: "26px",
                    padding: "0 13px",
                    fontSize: "12px",
                    borderRadius: "9px",
                    border: topBarDark
                      ? "1px solid black"
                      : "1px solid transparent",
                    margin: 0,
                  }}
                  animateIn
                  darkHover={!topBarDark}
                  onClick={() => {
                    trackEvent("/topbar/create_room");
                    // get everything after the ? if there is anything
                    const extraUrl = window.location.search;
                    handleCreateRoom(user, extraUrl);
                    setIsProfileMenuOpen(false);
                  }}
                >
                  {user && !hasFullAccess(user) ? "🔒 Room" : "+ Room"}
                </Button>
              )}
              <Button
                id="create-room-with-question-button"
                style={{
                  height: "0px",
                  width: "0px",
                  pointerEvents: "none",
                  padding: "0px",
                  opacity: 0,
                }}
                onClick={async () => {
                  await handleCreateRoom(user, "?useQuestion=true");
                  setIsProfileMenuOpen(false);
                }}
              >
                + Room
              </Button>
            </>
          )}
        {!isIOSNativeApp() && !isRouletteRoom && profilePic && (
          <StyledProfilePic
            css={{ $$size: "30px", $$padding: "90px" }}
            onClick={() => {
              setIsProfileMenuOpen(!isProfileMenuOpen);
              setIsNotificationMenuOpen(false);
            }}
            className="profile-pic"
            src={imageUrl(`/profile-pics/${profilePic}`, 60)}
          />
        )}
      </div>
      {/* Notification dropdown */}
      {isNotificationMenuOpen && (
        <div
          className="notification-menu"
          style={{
            position: 'absolute',
            width: '240px',
            maxHeight: '400px',
            overflowY: 'auto',
            right: isIOSNativeApp() ? '14px' : '50px',
            top: 'calc(var(--navHeight, 60px) - 6px)',
            backgroundColor: 'var(--colors-surface)',
            border: '1px solid var(--colors-border)',
            borderRadius: '16px',
            padding: '8px 0',
            zIndex: 1000,
          }}
        >
          <Button
            role="menuitem"
            className="arrow-button"
            Theme={topBarDark ? "dark" : "light"}
            style={{
              padding: "2px 6px",
              position: "absolute",
              right: "7px",
              top: "8px",
              transform: "rotate(90deg)",
              color: topBarDark ? "white" : "black",
            }}
            transparent
            onClick={() => {
              setIsNotificationMenuOpen(false);
            }}
          >
            &larr;
          </Button>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--colors-subtleFill)' }}>
            <span style={{ fontWeight: '500', fontSize: '11px', color: 'var(--colors-text)' }}>
              New
            </span>
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--colors-textMuted)', fontSize: '11px' }}>
              No post notifications yet
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className="notification-item"
                onClick={() => {
                  if (notification.postId) {
                    setLocation(`/post/${notification.postId}`);
                    setIsNotificationMenuOpen(false);
                  }
                }}
                style={{
                  padding: '10px 10px 12px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: notification.postId ? 'pointer' : 'default',
                  backgroundColor: !notification.isRead ? 'var(--colors-subtleFill)' : 'transparent',
                }}
              >
                {notification.actor.profilePic ? (
                  <StyledProfilePic
                    css={{ $$size: '28px', $$padding: '90px' }}
                    src={imageUrl(`/profile-pics/${notification.actor.profilePic}`, 56)}
                    style={{ flexShrink: 0 }}
                  />
                ) : (
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#4a4a4a',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', color: 'var(--colors-text)', lineHeight: '1.4' }}>
                    <span style={{ fontWeight: '600' }}>{notification.actor.name}</span>
                    {' '}
                    {notification.type === 'comment'
                      ? 'interpreted your reading'
                      : notification.type === 'thread_comment'
                        ? 'replied to a reading you interpreted'
                        : 'liked your interpretation'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--colors-textMuted)', marginTop: '2px' }}>
                    {formatTimeAgo(notification.createdAt).replace(' ago', '')}
                  </div>
                </div>
                {!notification.isRead && (
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      backgroundColor: '#ff564d',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}
      {!isIOSNativeApp() && isProfileMenuOpen && (
        <div className="profile-menu">
          <Button
            role="menuitem"
            className="arrow-button"
            Theme={topBarDark ? "dark" : "light"}
            style={{
              padding: "2px 6px",
              position: "absolute",
              right: "10px",
              top: "8px",
              transform: "rotate(90deg)",
              color: topBarDark ? "white" : "black",
            }}
            transparent
            onClick={() => {
              setIsProfileMenuOpen(false);
            }}
          >
            &larr;
          </Button>
          <ProfileMenuItem
            text={"Profile"}
            src={"/images/profile.svg"}
            onClick={() => {
              if (isInRoom || isConnecting)
                window.open(
                  window.location.protocol +
                    "//" +
                    window.location.host +
                    "/profile/" +
                    user?.username,
                  "_blank"
                );
              else setLocation("/profile/" + user?.username);
              setIsProfileMenuOpen(false);
            }}
          ></ProfileMenuItem>
          <ProfileMenuItem
            text={"New room"}
            src={"/images/plus.svg"}
            onClick={() => {
              handleCreateRoom(user);
              setIsProfileMenuOpen(false);
            }}
          ></ProfileMenuItem>
          {location !== "/" && (
            <ProfileMenuItem
              text={"Return home"}
              src={"/images/return-home.svg"}
              onClick={() => {
                setLocation("/");
                setIsProfileMenuOpen(false);
              }}
            ></ProfileMenuItem>
          )}
          <ProfileMenuItem
            text={"Journal"}
            src={"/images/info-icon.svg"}
            onClick={() => {
              setLocation("/journal");
              setIsProfileMenuOpen(false);
            }}
          ></ProfileMenuItem>
          <ProfileMenuItem
            text={"Invites"}
            src={"/images/more-menu/invite.svg"}
            onClick={() => {
              trackEvent("/topbar/pressed_invites");
              setLocation("/invites");
              setIsProfileMenuOpen(false);
            }}
          ></ProfileMenuItem>
          <ProfileMenuItem
            text={"Feedback"}
            src={"/images/feedback.svg"}
            onClick={() => {}}
          ></ProfileMenuItem>
          <ProfileMenuItem
            text={"Guidelines"}
            src={"/images/more-menu/blog.svg"}
            onClick={() => {
              window.open(
                "https://help.moonlight.world/article/52-moonlight-community-guidelines",
                "_blank"
              );
            }}
          ></ProfileMenuItem>
          <ProfileMenuItem
            text={"About"}
            src={"/images/about.svg"}
            onClick={() => {
              window.open("https://www.moonlight.world", "_blank");
            }}
          ></ProfileMenuItem>
          <StyledPlusMenuItem
            className="plus-menu-item"
            isOnPlusPlan={isOnPlusPlan}
            onClick={() => {
              // if on homepage, open in the same tab
              if (window.location.pathname === "/") {
                window.location.href = "/plans";
                return;
              }
              window.open(
                window.location.protocol +
                  "//" +
                  window.location.host +
                  "/plans",
                "_blank"
              );
            }}
          >
            {isOnPlusPlan ? "PLANS" : "PLANS"}
          </StyledPlusMenuItem>
          <div
            style={{
              width: "calc(100% + 16px)",
              marginLeft: "-8px",
              marginTop: "12px",
              marginBottom: "8px",
              height: "1px",
              backgroundColor: "var(--colors-subtleFill)",
            }}
          />
          <ProfileMenuItem
            text={"Sign out"}
            src={"/images/signout.svg"}
            onClick={() => {
              signout();
              setLocation("/signin");
              setIsProfileMenuOpen(false);
            }}
          ></ProfileMenuItem>
        </div>
      )}
    </div>
  );
  const isOnCardsPage = location.startsWith("/guidebook") || location.startsWith("/decks");

  return (
    <StyledTopBar
      darkMode={topBarDark}
      isInRoom={isInRoom}
      isIOSNative={isIOSNativeApp()}
      isOnCardsPage={isOnCardsPage}
      data-darkmode={topBarDark}
    >
      <div
        className="logo-container"
        style={{ display: "flex", alignItems: "center" }}
      >
        {!isIOSNativeApp() && (
          <img
            className="moonlight-logo"
            src={imageUrl("/moonlight_logo(black).png")}
            style={{ 
              cursor: "pointer",
              marginLeft: "11px"
            }}
            onClick={() => {
              if (isInRoom || isConnecting) {
                if (isOnMobile) return;

                window.open(
                  window.location.protocol + "//" + window.location.host,
                  "_blank"
                );
              } else {
                // if was already /, reload
                if (window.location.pathname === "/") {
                  window.location.reload();
                } else {
                  setLocation("/");
                }
              }
            }}
          />
        )}
        {isIOSNativeApp() && (
          <img
            className="moonlight-logo ios-native-logo"
            src={imageUrl("/moonlight_logo(black).png")}
          />
        )}
        {!isIOSNativeApp() && (
          <span id="new-place" style={{ 
            fontSize: "12px", 
            marginLeft: "14px"
          }}>
            A new place for tarot
          </span>
        )}
      </div>

      <div className="right-container">
        {user ? (
            profileMenu
        ) : (
          // Not logged in - existing logic
          <>
            {!window.location.pathname.includes("profile") &&
             !window.location.pathname.includes("plans") &&
             !window.location.pathname.includes("journal") &&
             !window.location.pathname.includes("daily") &&
             !window.location.pathname.includes("game") &&
             !window.location.pathname.includes("community") && (
              <Button
                id="cart-button"
                Theme={topBarDark ? "dark" : "light"}
                style={{
                  height: "26px",
                  padding: "0",
                  width: "72px",
                  fontSize: "12px",
                  borderRadius: "9px",
                  margin: 0,
                  marginRight: "4px",
                  border: !topBarDark
                    ? "1px solid black"
                    : "1px solid white",
                }}
                animateIn
                onClick={() => {
                  localState.cartPage = CartPage.CART;
                }}
              >
                <img src="/images/cart.svg" style={{ filter: topBarDark ? "invert(1)" : "none" }} />
                {currCartIds.length > 0 ? `${currCartIds.length}` : ""}
              </Button>
            )}
            <Button
              id="create-room-button"
              style={{
                width: 0,
                height: 0,
                pointerEvents: "none",
                opacity: 0,
                padding: 0,
                margin: 0,
                marginLeft: "-2px",
              }}
              onClick={() => {
                handleCreateRoom(user);
                setIsProfileMenuOpen(false);
              }}
            >
              + Room
            </Button>
            <Button
              id="create-room-with-question-button"
              style={{
                height: "0px",
                width: "0px",
                pointerEvents: "none",
                padding: "0px",
                opacity: 0,
              }}
              onClick={async () => {
                await handleCreateRoom(user, "?useQuestion=true");
                setIsProfileMenuOpen(false);
              }}
            >
              + Room
            </Button>
            <Button
              Theme={topBarDark ? "dark" : "light"}
              style={{
                height: "26px",
                padding: "0",
                width: "72px",
                fontSize: "12px",
                borderRadius: "9px",
                marginRight: "4px",
              }}
              animateIn
              onClick={() => {
                window.location.href = "/signin";
              }}
            >
              Log in
            </Button>
            <Button
              animateIn
              id="sign-up-button"
              style={{
                height: "26px",
                padding: "0",
                width: "72px",
                fontSize: "12px",
                borderRadius: "9px",
                marginRight: "16px",
                backgroundColor: topBarDark ? "white" : "black",
                color: topBarDark ? "black" : "white",
              }}
              onClick={() => {
                window.location.href = "/signup";
              }}
            >
              Sign up
            </Button>
          </>
        )}
      </div>
    </StyledTopBar>
  );
};

const StyledLinkInput = styled("input", {
  "&:focus": {
    outline: "none",
  },
});

const StyledPlusMenuItem = styled("div", {
  backgroundColor: "$lightPurple",
  fontSize: "11px",
  fontStyle: "italic",
  padding: "6px",
  borderRadius: "50px",
  textAlign: "center",
  maxWidth: "calc(100% - 16px)",
  marginLeft: "8px",
  marginTop: "4px",
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "$lightPurpleHover",
  },
  variants: {
    isOnPlusPlan: {
      true: {
        backgroundColor: "$gray100",

        "&:hover": {
          backgroundColor: "$gray200",
        },
      },
      false: {},
    },
  },
});

const StyledProfileMenuItem = styled("div", {
  fontSize: "12px",
  padding: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  color: "$text",
  "&:hover": {
    backgroundColor: "$subtleFill",
    borderRadius: "12px",
  },
  "& .icon": {
    width: "20px",
    height: "20px",
    margin: 0,
    marginRight: "10px",
    [`.${darkTheme.className} &`]: {
      filter: "invert(1)",
    },
  },
});
const fadein = keyframes({
  "0%": { opacity: 0 },
  "50%": { opacity: 0 },
  "100%": { opacity: 1 },
});
const StyledTopBar = styled("div", {
  width: "100%",
  height: "$navHeight",
  backgroundColor: "$washA500",
  zIndex: "1001",
  borderBottom: "1px solid rgba(0,0,0,0.5)",
  position: "fixed",
  top: 0,
  left: 0,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  "& img": {
    width: "117px",
    marginLeft: "11px",
  },
  "& .profile-pic": {
    marginRight: "12px",
    marginLeft: "6px",
    marginTop: "0",
    cursor: "pointer",
    animation: `${fadein} 1s ease-in-out`,
  },
  "& .profile-menu": {
    position: "absolute",
    width: "150px",
    right: "12px",
    top: "calc($navHeight + 16px)",
    backgroundColor: "$surface",
    border: "1px solid $border",
    borderRadius: "16px",
    padding: "26px 8px 16px 8px ",

    "& .menu-item": {
      fontSize: "12px",
      padding: "8px",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "$subtleFill",
        borderRadius: "12px",
      },
    },
  },

  "& #cart-button": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "40px !important",
    "& img": {
      width: "20px",
      marginLeft: "1px",
      marginRight: "1px",
    },
  },
  "& #notification-button": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "40px !important",
    "& svg": {
      width: "20px",
      height: "20px",
      flexShrink: 0,
      minWidth: "20px",
      minHeight: "20px",
    },
  },
  "& .notification-menu": {
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "6px",
      backgroundColor: "rgba(127, 127, 127, .5)",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
      marginTop: "16px",
      marginBottom: "16px",
    },
    "& .notification-item": {
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: "$subtleFill !important",
      },
    },
  },
  "@mobile": {
    height: "50px",
  },

  "& .logo-container": {
    "@mobile": {
      display: "flex",
      flexDirection: "column",
      "& img:not(.ios-native-logo)": {
        marginLeft: "-4px!important",
      },
    },
  },
  "& .right-container": {
    display: "flex",
    "& button": {
      textWrap: "nowrap",
    },
  },
  variants: {
    darkMode: {
      true: {
        backgroundColor: "$darkwashA500",
        borderBottom: "1px solid rgba(255, 255, 255,0.5)",
        "& .moonlight-logo": {
          filter: "invert(1)",
        },
        "& #new-place": {
          color: "white",
        },
      },
      false: {},
    },
    isIOSNative: {
      true: {
        backgroundColor: "$wash", // 100% opaque
        "&[data-darkmode='true']": {
          backgroundColor: "$darkwash", // 100% opaque dark
        },
      },
      false: {},
    },
    isInRoom: {
      true: {
        // borderBottom: "none",
        // pointerEvents: "none",
        // "& button": {
        //   pointerEvents: "all",
        // },
        // "& .profile-pic": {
        //   pointerEvents: "all",
        // },
      },
    },
    isOnCardsPage: {
      true: {
        "@mobile": {
          backgroundColor: "$wash",
          "&[data-darkmode='true']": {
            backgroundColor: "$darkwash",
          },
        },
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      isIOSNative: true,
      isInRoom: true,
      css: {
        backgroundColor: "$washA500",
        "&[data-darkmode='true']": {
          backgroundColor: "$darkwashA500", 
        },
        "& .right-container": {
          marginRight: "10px",
        },
      },
    },
  ],
  "& .custom-button": {
    position: "absolute",
    bottom: 0,
    right: 0,
  },
});
function updateRoomList() {
  throw new Error("Function not implemented.");
}


